import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import Footer from '@/pages/home/components/Footer';
import { articleContentMap, type ArticleSection } from '@/mocks/articleContent';
import { guides } from '@/mocks/guides';

function renderSection(section: ArticleSection, idx: number) {
  switch (section.type) {
    case 'heading':
      if (section.headingLevel === 'h2') {
        return (
          <h2 key={idx} className="font-heading text-2xl font-bold text-foreground-950 mb-4 mt-10 first:mt-0">
            {section.heading}
          </h2>
        );
      }
      if (section.headingLevel === 'h3') {
        return (
          <h3 key={idx} className="font-heading text-xl font-bold text-foreground-950 mb-3 mt-6">
            {section.heading}
          </h3>
        );
      }
      return (
        <h4 key={idx} className="text-sm font-semibold text-foreground-900 mb-1.5 mt-4">
          {section.heading}
        </h4>
      );

    case 'paragraph':
      return (
        <p key={idx} className="text-foreground-700 leading-relaxed mb-4">
          {section.text}
        </p>
      );

    case 'table':
      return (
        <div key={idx} className="overflow-x-auto my-6">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-primary-50">
                {section.tableData!.headers.map((h, i) => (
                  <th key={i} className={`text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200/70 ${i === 0 ? '' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.tableData!.rows.map((row, ri) => (
                <tr key={ri} className={`border-b border-background-200/70 ${ri % 2 === 1 ? 'bg-background-50' : ''}`}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3 ${ci === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'list':
      return (
        <ul key={idx} className="space-y-3 my-6">
          {section.items!.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-arrow-right-s-fill text-secondary-500"></i>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'numbered-list':
      return (
        <div key={idx} className="space-y-5 my-6">
          {section.numberedItems!.map((item, i) => (
            <div key={i} className="flex gap-4">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">{i + 1}</span>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                <p className="text-xs text-foreground-600 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case 'faq':
      return (
        <div key={idx} className="space-y-3 my-6">
          {section.faqItems!.map((faq, i) => (
            <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
              <h4 className="text-sm font-semibold text-foreground-900 mb-1.5">{faq.question}</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      );

    case 'card-grid':
      return (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          {section.cards!.map((card, i) => (
            <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
              <h4 className="text-sm font-semibold text-foreground-900 mb-2">{card.title}</h4>
              <p className="text-xs text-foreground-600 leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      );

    case 'callout':
      return (
        <div key={idx} className="bg-background-100 rounded-lg p-5 border border-background-200/70 my-6">
          <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">{section.heading}</h3>
          <ul className="space-y-2">
            {section.items!.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-primary-500 text-xs"></i>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug && articleContentMap[slug] ? articleContentMap[slug] : null;

  const relatedGuides = guides
    .filter((g) => g.slug !== `/article/${slug}`)
    .slice(0, 4);

  if (!article) {
    return (
      <>
        <PageMeta
          title="Article Not Found - LitBuy Spreadsheets"
          description="The article you are looking for is not available yet. Browse our collection of LitBuy spreadsheet guides and resources."
          path={`/article/${slug}`}
          ogType="website"
        />
        <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="font-heading text-lg font-extrabold text-foreground-950 whitespace-nowrap">
                LitBuy Spreadsheets
              </Link>
              <Link to="/" className="text-sm font-semibold text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-arrow-left-line text-xs"></i></span>
                Back to Home
              </Link>
            </div>
          </div>
        </nav>
        <div className="min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center py-20">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-article-line text-2xl"></i>
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Article Not Found</h2>
            <p className="text-foreground-500 mb-6">The article you are looking for is not available yet. Check back soon.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = (siteUrl: string) => {
    const faqSections = article.sections.filter(
      (s) => s.type === 'faq' && s.faqItems && s.faqItems.length > 0
    );

    const graph: Record<string, unknown>[] = [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.date,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'LitBuy Spreadsheets',
        },
        url: `${siteUrl}/article/${slug}`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/article/${slug}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.title,
            item: `${siteUrl}/article/${slug}`,
          },
        ],
      },
    ];

    if (faqSections.length > 0) {
      const allFaqItems = faqSections.flatMap((s) => s.faqItems!);
      graph.push({
        '@type': 'FAQPage',
        mainEntity: allFaqItems.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      });
    }

    return { '@context': 'https://schema.org', '@graph': graph };
  };

  return (
    <>
      <PageMeta
        title={article.title}
        description={article.description}
        path={`/article/${slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-heading text-lg font-extrabold text-foreground-950 whitespace-nowrap">
              LitBuy Spreadsheets
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-semibold text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-arrow-left-line text-xs"></i></span>
                Back to Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-4 py-2 text-xs font-bold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <article className="min-h-screen">
        <header className="bg-background-50 py-12 md:py-16 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 mb-4">
                {article.tag}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                {article.description}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                  {article.author}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              {article.sections.map((section, idx) => (
                <section key={idx} className="mb-10">
                  {renderSection(section, idx)}
                </section>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 p-5 rounded-lg bg-accent-50 border border-accent-200/60">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-user-line text-xl"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">LitBuy Spreadsheets Editorial Team</p>
                  <p className="text-xs text-foreground-500 mt-0.5">Independent educational resource dedicated to helping users master spreadsheet management, data organization, and tracking workflows.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {relatedGuides.length > 0 && (
          <div className="bg-background-100 py-10 mt-10">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="max-w-3xl mx-auto">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-4">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {relatedGuides.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={guide.slug}
                      className="p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300/60 transition-colors cursor-pointer"
                    >
                      <span className="text-xs font-semibold text-accent-600">{guide.tag}</span>
                      <h4 className="text-sm font-semibold text-foreground-900 mt-1 mb-1">{guide.title}</h4>
                      <p className="text-xs text-foreground-500">{guide.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </article>

      <Footer />
    </>
  );
}